import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { CheckIcon, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type PhoneInputProps = Omit<
  React.ComponentProps<"input">,
  "onChange" | "value" | "ref"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange?: (value: RPNInput.Value) => void;
  };

const PhoneInput = React.forwardRef<
  React.ElementRef<typeof RPNInput.default>,
  PhoneInputProps
>(({ className, onChange, value, ...props }, ref) => {
  return (
    <RPNInput.default
      ref={ref}
      className={cn("flex", className)}
      flagComponent={FlagComponent}
      countrySelectComponent={CountrySelect}
      inputComponent={InputComponent}
      smartCaret={false}
      value={value || undefined}
      onChange={(val: RPNInput.Value) =>
        onChange?.(val || ("" as RPNInput.Value))
      }
      {...props}
    />
  );
});

PhoneInput.displayName = "PhoneInput";

const InputComponent = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, ...props }, ref) => (
  <Input
    className={cn("rounded-e-lg rounded-s-none", className)}
    {...props}
    ref={ref}
  />
));

InputComponent.displayName = "PhoneInputInput";

type CountryEntry = { label: string; value: string | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: string;
  options: CountryEntry[];
  onChange: (country: string) => void;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
}: CountrySelectProps) => {
  const [searchValue, setSearchValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const filteredOptions = React.useMemo(() => {
    if (!searchValue.trim()) return countryList;
    const search = searchValue.toLowerCase();
    return countryList.filter(
      ({ label, value }) =>
        value &&
        (label.toLowerCase().includes(search) ||
          value.toLowerCase().includes(search)),
    );
  }, [countryList, searchValue]);

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (open) setSearchValue("");
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="flex gap-1 rounded-e-none rounded-s-lg border-r-0 px-3 focus:z-10"
          disabled={disabled}
        >
          <FlagComponent
            country={selectedCountry}
            countryName={selectedCountry}
          />
          <ChevronsUpDown
            className={cn(
              "-mr-1 size-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72 p-2">
        <div className="mb-2">
          <Input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Busca un país..."
            className="h-8 text-xs"
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              Sin resultados.
            </div>
          ) : (
            filteredOptions.map(({ value, label }) =>
              value ? (
                <CountrySelectOption
                  key={value}
                  country={value}
                  countryName={label}
                  selectedCountry={selectedCountry}
                  onChange={onChange}
                  onSelectComplete={() => setIsOpen(false)}
                />
              ) : null,
            )
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface CountrySelectOptionProps extends RPNInput.FlagProps {
  selectedCountry: string;
  onChange: (country: string) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const isSelected = country === selectedCountry;

  return (
    <DropdownMenuItem
      key={country}
      onSelect={() => {
        onChange(country as string);
        onSelectComplete();
      }}
      className="flex items-center gap-2 text-sm"
    >
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 truncate">{countryName}</span>
      {isSelected && <CheckIcon className="ml-2 size-4" />}
    </DropdownMenuItem>
  );
};

const FlagComponent = ({ country, countryName }: RPNInput.FlagProps) => {
  if (!country) return null;
  const Flag = flags[country as keyof typeof flags];
  if (!Flag) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <Flag title={countryName} className="size-4 rounded-sm shadow-xs" />
      <span className="text-xs font-medium uppercase text-muted-foreground">
        {country}
      </span>
    </span>
  );
};

export { PhoneInput };
